import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPublications, getPublication } from "../../api/publicationsApi";
import { getIssues, getIssue } from "../../api/issuesApi";
import { getPinnedHomeCards, pinHomeCard, unpinHomeCard, reorderPinnedHomeCards } from "../../api/pinnedHomeApi";
import { translateAuthError } from "../../api/authApi";

const SOURCE_LABELS = { topic: "نشریه", issue_topic: "شماره" };

// Section C (admin-only): picks which کارت‌ها — from either a نشریه
// (Publication) or a شماره (Issue), the site's two separate card
// hierarchies — additionally appear in the homepage's "مقاله‌های منتخب"
// row, ahead of the row's automatically-listed articles (see
// HomePage.js). Unlike AdminFeaturedPage.js's three fixed hero-poster
// slots, there's no fixed count here — any number of cards from either
// source can be pinned, in whatever order the admin arranges via the
// move up/down buttons.
function AdminPinnedHomePage() {
  const [publications, setPublications] = useState([]);
  const [issues, setIssues] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [sourceType, setSourceType] = useState("topic");
  const [sourceId, setSourceId] = useState(""); // publication slug or issue id, depending on sourceType
  const [candidates, setCandidates] = useState([]);
  const [cardId, setCardId] = useState("");
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");

  function load() {
    setLoading(true);
    Promise.all([getPublications(), getIssues(), getPinnedHomeCards()])
      .then(([pubList, issueList, pinnedList]) => {
        setPublications(pubList);
        setIssues(issueList);
        setPinned(pinnedList);
      })
      .catch((err) => setError(translateAuthError(err.message)))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  // Re-fetches the card candidates whenever the admin picks a
  // different نشریه/شماره to pull from, or switches which of the two
  // they're pulling from at all.
  useEffect(() => {
    if (!sourceId) {
      setCandidates([]);
      setCardId("");
      return;
    }
    setLoadingCandidates(true);
    const fetchTopics = sourceType === "topic" ? getPublication(sourceId) : getIssue(sourceId);
    fetchTopics
      .then((parent) => setCandidates(parent.topics))
      .catch(() => setCandidates([]))
      .finally(() => setLoadingCandidates(false));
  }, [sourceType, sourceId]);

  const pinnedKeys = new Set(pinned.map((p) => `${p.kind}:${p.id}`));
  const pinnableCandidates = candidates.filter((t) => !pinnedKeys.has(`${sourceType}:${t.id}`));

  function handleSourceTypeChange(nextType) {
    setSourceType(nextType);
    setSourceId("");
  }

  async function handleAdd(event) {
    event.preventDefault();
    if (!cardId) return;
    setError("");
    setSubmitting(true);
    try {
      await pinHomeCard(token, sourceType, Number(cardId));
      setSourceId("");
      load();
    } catch (err) {
      setError(translateAuthError(err.message));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(kind, id) {
    if (!window.confirm("این کارت از «مقاله‌های منتخب» حذف شود؟")) return;
    setError("");
    try {
      await unpinHomeCard(token, kind, id);
      load();
    } catch (err) {
      setError(translateAuthError(err.message));
    }
  }

  // Swaps the pinned card at `index` with its neighbor in `direction`
  // (-1 up / +1 down), then submits the full new order. Applied to
  // local state immediately and rolled back only if the request fails,
  // same pattern as IssueTopicsPage.js/PublicationTopicsPage.js.
  async function moveCard(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= pinned.length) return;

    const reordered = [...pinned];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setPinned(reordered);

    try {
      await reorderPinnedHomeCards(token, reordered.map((p) => ({ kind: p.kind, id: p.id })));
    } catch (err) {
      setError(translateAuthError(err.message));
      setPinned(pinned);
    }
  }

  return (
    <div className="admin-page">
      <Link to="/admin" className="back-link">
        ← بازگشت به پنل مدیریت
      </Link>

      <div className="admin-page-header">
        <h1>کارت‌های نشریه/شماره در «مقاله‌های منتخب»</h1>
      </div>

      <p className="admin-empty">
        این کارت‌ها — از هر نشریه یا هر شمارهٔ دریچه — پیش از مقاله‌هایی که به‌طور خودکار نمایش داده می‌شوند، در
        همان ردیف «مقاله‌های منتخب» صفحهٔ اصلی ظاهر می‌شوند.
      </p>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <p>در حال بارگذاری...</p>
      ) : (
        <>
          <div className="admin-topic-section">
            <h2>کارت‌های پین‌شده</h2>

            {pinned.length === 0 && <p className="admin-empty">هنوز کارتی پین نشده است.</p>}

            <ul className="admin-article-list">
              {pinned.map((p, index) => (
                <li key={`${p.kind}:${p.id}`} className="admin-article-row">
                  <div className="admin-reorder-buttons">
                    <button
                      type="button"
                      className="admin-reorder-button"
                      disabled={index === 0}
                      onClick={() => moveCard(index, -1)}
                      aria-label="انتقال به بالا"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="admin-reorder-button"
                      disabled={index === pinned.length - 1}
                      onClick={() => moveCard(index, 1)}
                      aria-label="انتقال به پایین"
                    >
                      ▼
                    </button>
                  </div>
                  <span className="admin-article-row-title">
                    [{SOURCE_LABELS[p.kind]}] {p.title_fa || p.title_en} — {p.category}
                  </span>
                  <div className="admin-actions">
                    <button type="button" onClick={() => handleRemove(p.kind, p.id)}>
                      حذف پین
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="admin-topic-section">
            <h2>افزودن کارت جدید</h2>

            <form onSubmit={handleAdd}>
              <div className="auth-field">
                <label htmlFor="pinned-home-source-type">منبع کارت</label>
                <select
                  id="pinned-home-source-type"
                  value={sourceType}
                  onChange={(e) => handleSourceTypeChange(e.target.value)}
                >
                  <option value="topic">نشریه</option>
                  <option value="issue_topic">شماره</option>
                </select>
              </div>

              <div className="auth-field">
                <label htmlFor="pinned-home-source-id">{sourceType === "topic" ? "نشریه" : "شماره"}</label>
                <select id="pinned-home-source-id" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                  <option value="">انتخاب کنید...</option>
                  {sourceType === "topic"
                    ? publications.map((pub) => (
                        <option value={pub.slug} key={pub.slug}>
                          {pub.title}
                        </option>
                      ))
                    : issues.map((iss) => (
                        <option value={iss.id} key={iss.id}>
                          {iss.title} (#{iss.number})
                        </option>
                      ))}
                </select>
              </div>

              <div className="auth-field">
                <label htmlFor="pinned-home-card">کارت</label>
                <select
                  id="pinned-home-card"
                  value={cardId}
                  onChange={(e) => setCardId(e.target.value)}
                  disabled={!sourceId || loadingCandidates}
                >
                  <option value="">{loadingCandidates ? "در حال بارگذاری..." : "انتخاب کنید..."}</option>
                  {pinnableCandidates.map((t) => (
                    <option value={t.id} key={t.id}>
                      {t.category} — {t.title_fa || t.title_en}
                    </option>
                  ))}
                </select>
              </div>

              <button type="submit" className="auth-submit" disabled={!cardId || submitting}>
                {submitting ? "در حال افزودن..." : "افزودن به مقاله‌های منتخب"}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminPinnedHomePage;
